import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { contractsApi, type Contract } from "../api/contracts";
import { useAuth } from "../auth/AuthContext";

/**
 * Surfaces contracts that need attention.
 * • Customers see contracts in SENT_TO_CUSTOMER (waiting for them to view).
 * • Sales/admins see CUSTOMER_VIEWED contracts they haven't moved to SIGNED yet.
 */
export function ContractsActionWidget({ limit = 5 }: { limit?: number }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [items, setItems] = useState<Contract[] | null>(null);

  useEffect(() => {
    const status = hasPermission("contracts:edit_lifecycle") ? "CUSTOMER_VIEWED" : "SENT_TO_CUSTOMER";
    void contractsApi
      .list({ status })
      .then((all) => setItems(all.slice(0, limit)))
      .catch(() => setItems([]));
  }, [hasPermission, limit]);

  const isSales = hasPermission("contracts:edit_lifecycle");
  const title = isSales
    ? t("widgets.contractsAction.titleSales")
    : t("widgets.contractsAction.titleCustomer");
  const allLink = isSales ? "/admin/contracts" : "/contracts";

  if (items === null) return null;
  return (
    <section style={card}>
      <header style={head}>
        <strong>{title}</strong>
        <Link to={allLink} className="widget-more-link" style={moreLink}>{t("widgets.contractsAction.viewAll")}</Link>
      </header>
      {items.length === 0 ? (
        <p style={empty}>
          {isSales
            ? t("widgets.contractsAction.emptySales")
            : t("widgets.contractsAction.emptyCustomer")}
        </p>
      ) : (
        <ul style={list}>
          {items.map((c) => (
            <li key={c.id}>
              <Link to={allLink} style={itemLink}>
                <strong style={{ color: "var(--brand-ink)", fontSize: 13 }}>{c.referenceNumber}</strong>
                <span style={{ display: "block", color: "var(--ink)", fontSize: 12 }}>{c.title}</span>
                <small style={{ color: "var(--ink-3)" }}>{c.companyName}</small>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)", padding: 16, borderRadius: 10, boxShadow: "var(--shadow-1)",
  display: "flex", flexDirection: "column", gap: 12,
};
const head: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--ink)", fontSize: 14 };
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 };
const itemLink: React.CSSProperties = { textDecoration: "none", display: "block", padding: 8, borderRadius: 6 };
const moreLink: React.CSSProperties = { color: "var(--brand-ink)", fontSize: 12, textDecoration: "none" };
const empty: React.CSSProperties = { color: "var(--ink-4)", fontSize: 13, margin: 0 };
